/**
 * Checkout Form Component
 *
 * Single-page checkout with address form and payment on the same screen.
 */

import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart, useAuth, useShop } from '@/context';
import { checkoutApi, shippingApi } from '@/api/client';
import { Button } from '../shared/Button';
import { AddressForm, type AddressFormRef } from './AddressForm';
import { OrderSummary } from './OrderSummary';
import { GuestIdentification } from './GuestIdentification';
import type { Address, CheckoutData, ShippingMethod } from '@/types';
import styles from './CheckoutForm.module.css';

type CheckoutPhase = 'form' | 'confirmation';

export function CheckoutForm() {
  const { cart, isEmpty, isLoading: cartLoading, clearCart } = useCart();
  const { user, isLoggedIn, refreshUser } = useAuth();
  const { formatPrice, t, urls, config } = useShop();

  const [phase, setPhase] = useState<CheckoutPhase>('form');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shipToDifferent, setShipToDifferent] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<number | null>(null);
  const [shippingCountry, setShippingCountry] = useState<string>('');
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [orderTotal, setOrderTotal] = useState<number | null>(null);

  const billingRef = useRef<AddressFormRef>(null);
  const shippingRef = useRef<AddressFormRef>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);

  // Load Stripe
  const stripePromise = useMemo(
    () => loadStripe(config.stripeKey),
    [config.stripeKey]
  );

  // Load full user data (including addresses) on mount
  useEffect(() => {
    if (isLoggedIn) {
      refreshUser();
    }
  }, [isLoggedIn]);

  // Check for order confirmation in URL (e.g. Stripe redirect after 3D Secure)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const key = params.get('key');
    const orderNum = params.get('order_number');

    if (key) {
      if (orderNum) {
        setOrderNumber(orderNum);
      }
      clearCart();
      setPhase('confirmation');
    }
  }, []);

  // Pre-fill from user data
  const defaultBilling: Partial<Address> = user
    ? {
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      ...user.billing_address,
    }
    : {};

  const defaultShipping: Partial<Address> = user?.shipping_address || {};

  // Fetch shipping methods when country changes
  const fetchShippingMethods = useCallback(async (country: string) => {
    if (!country || country === shippingCountry) return;
    setShippingCountry(country);
    setLoadingShipping(true);
    setSelectedShippingMethod(null);
    try {
      const result = await shippingApi.getMethods(country);
      setShippingMethods(result.methods);
      if (result.methods.length === 1) {
        setSelectedShippingMethod(result.methods[0].id);
      }
    } catch {
      setShippingMethods([]);
    } finally {
      setLoadingShipping(false);
    }
  }, [shippingCountry]);

  // Track country from billing or shipping address
  const handleBillingChange = useCallback((values: Partial<Address>) => {
    if (!shipToDifferent && values.country) {
      fetchShippingMethods(values.country);
    }
  }, [shipToDifferent, fetchShippingMethods]);

  const handleShippingChange = useCallback((values: Partial<Address>) => {
    if (values.country) {
      fetchShippingMethods(values.country);
    }
  }, [fetchShippingMethods]);

  // When toggling shipToDifferent, re-fetch based on appropriate address
  useEffect(() => {
    if (!shipToDifferent) {
      const billingCountry = billingRef.current?.getValues()?.country;
      if (billingCountry) fetchShippingMethods(billingCountry);
    } else {
      const shippingValues = shippingRef.current?.getValues();
      if (shippingValues?.country) fetchShippingMethods(shippingValues.country);
    }
  }, [shipToDifferent]);

  // Create PaymentIntent when addresses are validated
  const preparePayment = async () => {
    if (!isLoggedIn) {
      setError('Veuillez d\'abord vous identifier.');
      return;
    }

    // Validate addresses first
    const billingValid = billingRef.current?.validate();
    const shippingValid = shipToDifferent ? shippingRef.current?.validate() : true;

    if (!billingValid || !shippingValid) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    if (shippingMethods.length > 0 && selectedShippingMethod === null) {
      setError('Veuillez choisir une méthode de livraison.');
      return;
    }

    const billing = billingRef.current!.getValues();
    // Email field is hidden (user already identified) — inject from user data
    if (user?.email && !billing.email) {
      billing.email = user.email;
    }
    const shipping = shipToDifferent
      ? shippingRef.current!.getValues()
      : billing;

    setIsLoading(true);
    setError(null);

    try {
      const checkoutData: CheckoutData = {
        billing,
        shipping,
        ship_to_different_address: shipToDifferent,
        ...(selectedShippingMethod !== null && { shipping_method: selectedShippingMethod }),
      };

      const response = await checkoutApi.createPaymentIntent(checkoutData);
      setClientSecret(response.client_secret);
      setOrderId(response.order_id);
      setOrderTotal(response.total);
      setPaymentReady(true);

      // Scroll to payment section
      setTimeout(() => {
        paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Erreur lors de la préparation du paiement';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = async (paymentIntentId: string) => {
    if (!orderId) return;

    setIsLoading(true);

    try {
      const response = await checkoutApi.completeOrder(orderId, paymentIntentId);
      setOrderNumber(response.order_number);
      await clearCart();
      await refreshUser(); // Fetch updated user data (including new addresses)
      setPhase('confirmation');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Erreur lors de la finalisation';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment error
  const handlePaymentError = (message: string) => {
    setError(message);
    setIsLoading(false);
  };

  // Refresh auth state after guest registers/logs in
  const handleAuthenticated = () => {
    window.location.reload();
  };

  // Loading skeleton
  if (cartLoading && isEmpty) {
    return (
      <div className={styles.container}>
        <div className={styles.main}>
          <div className={styles.skeletonSection}>
            <div className={styles.skeletonSectionTitle} />
            <div className={styles.skeletonFields}>
              <div className={styles.skeletonFieldRow}>
                <div className={styles.skeletonField} />
                <div className={styles.skeletonField} />
              </div>
              <div className={styles.skeletonField} />
              <div className={styles.skeletonField} />
            </div>
          </div>
          <div className={styles.skeletonButton} />
        </div>
        <aside className={styles.sidebar}>
          <div className={styles.skeletonSummary}>
            <div className={styles.skeletonSummaryTitle} />
            <div className={styles.skeletonSummaryItem} />
            <div className={styles.skeletonSummaryItem} />
            <div className={styles.skeletonSummaryTotal} />
          </div>
        </aside>
      </div>
    );
  }

  // Empty cart
  if (isEmpty && phase !== 'confirmation') {
    return (
      <div className={styles.empty}>
        <p>{t('emptyCart')}</p>
        <Button variant="primary" onClick={() => (window.location.href = urls.shop)}>
          Continuer mes achats
        </Button>
      </div>
    );
  }

  // Confirmation phase
  if (phase === 'confirmation') {
    return (
      <div className={styles.confirmation}>
        <div className={styles.confirmationIcon}>
          <CheckIcon />
        </div>
        <h2>{t('orderComplete')}</h2>
        <p>{t('thankYou')}</p>
        {orderNumber && (
          <p className={styles.orderNumber}>
            Commande n° <strong>{orderNumber}</strong>
          </p>
        )}
        <p className={styles.confirmationNote}>
          Un email de confirmation vous a été envoyé.
        </p>
        <div className={styles.confirmationActions}>
          <Button
            variant="primary"
            onClick={() => (window.location.href = urls.account)}
          >
            {t('myOrders')}
          </Button>
          <Button
            variant="outline"
            onClick={() => (window.location.href = urls.shop)}
          >
            Continuer mes achats
          </Button>
        </div>
      </div>
    );
  }

  // Main checkout form
  return (
    <div className={styles.container}>
      <div className={styles.main}>
        {/* Guest Identification */}
        {!isLoggedIn && (
          <GuestIdentification onAuthenticated={handleAuthenticated} />
        )}

        {/* Error display */}
        {error && (
          <div className={styles.error}>
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Sections disabled until guest is identified */}
        <div className={!isLoggedIn ? styles.disabledSection : undefined}>

        {/* Billing Address */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('billing')}</h3>
          <AddressForm
            ref={billingRef}
            type="billing"
            defaultValues={defaultBilling}
            showEmail={false}
            showPhone
            onChange={handleBillingChange}
          />
        </div>

        {/* Ship to different address checkbox */}
        <div className={styles.checkbox}>
          <input
            type="checkbox"
            id="ship-different"
            checked={shipToDifferent}
            onChange={(e) => setShipToDifferent(e.target.checked)}
          />
          <label htmlFor="ship-different">
            Livrer à une adresse différente
          </label>
        </div>

        {/* Shipping Address (if different) */}
        {shipToDifferent && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{t('shipping')}</h3>
            <AddressForm
              ref={shippingRef}
              type="shipping"
              defaultValues={defaultShipping}
              showEmail={false}
              showPhone={false}
              onChange={handleShippingChange}
            />
          </div>
        )}

        {/* Shipping Method Selection */}
        {shippingMethods.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Méthode de livraison</h3>
            <div className={styles.shippingMethods}>
              {shippingMethods.map((method) => (
                <label key={method.id} className={`${styles.shippingMethod} ${selectedShippingMethod === method.id ? styles.shippingMethodSelected : ''}`}>
                  <input
                    type="radio"
                    name="shipping_method"
                    value={method.id}
                    checked={selectedShippingMethod === method.id}
                    onChange={() => setSelectedShippingMethod(method.id)}
                  />
                  <div className={styles.shippingMethodInfo}>
                    <span className={styles.shippingMethodLabel}>{method.label}</span>
                    {method.estimated_delivery && (
                      <span className={styles.shippingMethodDays}>
                        {method.estimated_delivery}
                      </span>
                    )}
                  </div>
                  <span className={styles.shippingMethodPrice}>
                    {method.price === 0 ? 'Gratuit' : formatPrice(method.price)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {loadingShipping && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Méthode de livraison</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>Chargement des méthodes disponibles...</p>
          </div>
        )}

        {/* Payment Section */}
        <div ref={paymentSectionRef} className={styles.section}>
          <h3 className={styles.sectionTitle}>Paiement sécurisé</h3>

          {!paymentReady ? (
            <div className={styles.paymentPlaceholder}>
              <LockIcon />
              <p>Validez vos informations pour afficher les options de paiement</p>
              <Button
                variant="outline"
                onClick={preparePayment}
                isLoading={isLoading}
              >
                Valider et continuer
              </Button>
            </div>
          ) : clientSecret ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#1a1a1a',
                    fontFamily: '"Work Sans", -apple-system, BlinkMacSystemFont, sans-serif',
                    borderRadius: '4px',
                  },
                },
                locale: 'fr',
              }}
            >
              <InlinePaymentForm
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                total={orderTotal ?? cart.totals.total}
                formatPrice={formatPrice}
              />
            </Elements>
          ) : null}
        </div>

        </div>{/* end disabledSection wrapper */}
      </div>

      <aside className={styles.sidebar}>
        <OrderSummary
          shippingMethod={shippingMethods.find(m => m.id === selectedShippingMethod) ?? null}
          orderTotal={orderTotal}
        />
      </aside>
    </div>
  );
}

/**
 * Inline Payment Form
 * Shows Stripe PaymentElement and handles submission
 */
interface InlinePaymentFormProps {
  onSuccess: (paymentIntentId: string) => void;
  onError: (message: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  total: number;
  formatPrice: (amount: number) => string;
}

function InlinePaymentForm({
  onSuccess,
  onError,
  isLoading,
  setIsLoading,
  total,
  formatPrice,
}: InlinePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [expressAvailable, setExpressAvailable] = useState(false);

  const confirmPayment = async () => {
    if (!stripe || !elements) return;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message || 'Erreur lors du paiement');
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else if (paymentIntent && paymentIntent.status === 'requires_action') {
      onError('Authentification requise. Veuillez suivre les instructions.');
    } else {
      onError('Le paiement n\'a pas pu être finalisé.');
    }
  };

  const handleExpressCheckout = async () => {
    if (!stripe || !elements) return;
    setIsLoading(true);

    try {
      await confirmPayment();
    } catch (err) {
      onError('Une erreur inattendue est survenue.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError(submitError.message || 'Erreur de validation');
        return;
      }

      await confirmPayment();
    } catch (err) {
      onError('Une erreur inattendue est survenue.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.paymentForm}>
      <ExpressCheckoutElement
        onConfirm={handleExpressCheckout}
        onReady={({ availablePaymentMethods }) => {
          setExpressAvailable(!!availablePaymentMethods);
        }}
        options={{
          paymentMethods: {
            applePay: 'always',
            googlePay: 'always',
          },
        }}
      />

      {expressAvailable && (
        <div className={styles.separator}>
          <span>ou payer par carte</span>
        </div>
      )}

      <PaymentElement
        options={{
          layout: 'tabs',
        }}
        onReady={() => setReady(true)}
      />

      <div className={styles.secureNote}>
        <LockIcon />
        <span>Paiement sécurisé par Stripe</span>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="large"
        fullWidth
        isLoading={isLoading}
        disabled={!stripe || !ready}
      >
        Payer {formatPrice(total)}
      </Button>
    </form>
  );
}

function CheckIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
